import React, { useState, useEffect, useMemo } from 'react';
import { FaGraduationCap } from 'react-icons/fa';
import { FiLock, FiPlayCircle } from 'react-icons/fi';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ExerciseIcon } from '../../branding/ExerciseIcon';
import { Button } from '../../components/Button';
import { Title } from '../../components/Title';
import { getExerciseProgressSummary } from '../../exercises/exerciseData';
import { initialExerciseProgress } from '../../exercises/initialExerciseProgress';
import { ShowSiteLoading } from '../../SiteLoadingIndicator';
import { ContentScreen } from '../../styles/ContentScreen';
import { assetsUrl, authStatuses } from '../../util/constants';
import { ModulesList } from '../ModulesList';
import { Table } from '../Table';

const ExerciseInfoStyles = styled(ContentScreen)`
   .exercise-info-progress-description .svg-icon {
      max-width: 124px;
      margin: 0 1em 1em 0;
      float: left;
   }

   @media (min-width: 500px) {
      .exercise-info-progress-description .svg-icon {
         max-width: 150px;
         margin: 0 2em 2em 0;
      }
   }

   @media (min-width: 650px) {
      .exercise-info-progress-description .svg-icon {
         max-width: 175px;
         margin: 0 3em 3em 0;
      }
   }

   .exercise-info-progress-description,
   .exercise-info-levels {
      margin-bottom: 3em;
   }

   .exercise-start-button-wrapper {
      margin-top: 2em;
      text-align: center;
      clear: left;
   }

   .levels-table {
      table-layout: fixed;
   }

   .level-row td {
      line-height: 1.3;
      vertical-align: baseline;
   }

   .level-number-cell {
      width: 60px;
      padding-right: 0;
   }

   .level-title-cell {
      text-overflow: ellipsis;
      overflow: hidden;
      padding-right: 0;
   }

   .level-row .exercise-actions-cell {
      vertical-align: top;
   }

   @media (min-width: 350px) {
      .level-number-cell {
         padding-right: 1em;
         width: 76px;
      }
   }
   @media (min-width: 450px) {
      .level-title-cell {
         padding-right: 1em;
      }
   }

   .exercise-accuracy-cell,
   .exercise-accuracy-head,
   .exercise-progress-cell,
   .exercise-progress-head {
      display: none;
   }

   @media (min-width: 450px) {
      .exercise-progress-cell,
      .exercise-progress-head {
         display: table-cell;
      }
   }

   @media (min-width: 650px) {
      .exercise-accuracy-cell,
      .exercise-accuracy-head {
         display: table-cell;
      }
   }
`;

const Level = ({
   title,
   hasTutorial,
   level,
   locked,
   linkToMainExercise,
   linkTutorialToMainExercise,
}) => {
   const { exerciseId, moduleId } = useParams();

   const modulePath = moduleId ? `/${moduleId}` : '';
   const mainExerciseLink = `/app/exercise/${exerciseId}${modulePath}`;
   const levelLink = `${mainExerciseLink}?level=${level}`;

   return (
      <tr className="level-row">
         <td className="level-number-cell">
            <span className="exercise-level-label">
               {level < 10 ? '0' : ''}
               {level}:
            </span>{' '}
         </td>
         <td className="level-title-cell">{title}</td>
         <td className="exercise-actions-cell">
            {!locked && (
               <Link
                  className="icon-link"
                  to={
                     linkTutorialToMainExercise
                        ? mainExerciseLink
                        : `${levelLink}&tutorial`
                  }
                  style={{ visibility: hasTutorial ? 'visible' : 'hidden' }}
               >
                  <FaGraduationCap size="1.5em" />
               </Link>
            )}
            {!locked && (
               <Link
                  className="icon-link"
                  to={linkToMainExercise ? mainExerciseLink : levelLink}
               >
                  <FiPlayCircle size="1.5em" />
               </Link>
            )}
            {locked && <FiLock size="1.5em" className="exercise-locked" />}
         </td>
      </tr>
   );
};

const LevelsList = ({ levels }) =>
   levels == null ? null : (
      <Table className="row-hover levels-table">
         <tbody>
            {levels.map(
               ({
                  title,
                  hasTutorial,
                  level,
                  locked,
                  linkToMainExercise,
                  linkTutorialToMainExercise,
               }) => (
                  <Level
                     key={level}
                     title={title}
                     hasTutorial={hasTutorial}
                     level={level}
                     locked={locked}
                     linkToMainExercise={linkToMainExercise}
                     linkTutorialToMainExercise={linkTutorialToMainExercise}
                  />
               )
            )}
         </tbody>
      </Table>
   );

const mapStateToProps = ({
   appData: {
      userData: { status },
   },
}) => ({
   authStatus: status,
});

const ExerciseInfoScreen = connect(mapStateToProps)(({ authStatus }) => {
   const { exerciseId, moduleId } = useParams();
   const [exerciseInfo, setExerciseInfo] = useState(null);
   const [exerciseProgressSummary, setExerciseProgressSummary] = useState(null);
   const [loadingNew, setLoadingNew] = useState(false);
   // const [modules, setModules] = useState(null);

   const isAuthenticated = authStatus === authStatuses.authenticated;

   const hasProgress =
      exerciseProgressSummary && exerciseProgressSummary.accuracy != null;

   const moduleInfoPath = moduleId ? `module/${moduleId}/` : '';
   const moduleExercisePath = moduleId ? `/${moduleId}` : '';

   const mappedLevels = useMemo(() => {
      if (exerciseInfo == null || exerciseProgressSummary == null) {
         return null;
      }

      return exerciseInfo.levels.map((level, i) => ({
         ...level,
         level: i + 1,
         locked:
            exerciseProgressSummary.locked ||
            exerciseProgressSummary?.currentLevel < i + 1,

         linkToMainExercise: exerciseProgressSummary?.currentLevel === i + 1,
         linkTutorialToMainExercise:
            exerciseProgressSummary?.currentLevel === i + 1 &&
            !exerciseProgressSummary.tutorialComplete,
      }));
   }, [exerciseInfo, exerciseProgressSummary]);

   useEffect(() => {
      if (exerciseInfo != null) {
         setLoadingNew(true);
      }
      Promise.all([
         fetch(
            `${assetsUrl}/public/exercise/${exerciseId}/${moduleInfoPath}exerciseInfo.json`
         ).then((res) => res.json()),
         isAuthenticated
            ? getExerciseProgressSummary(exerciseId)
            : Promise.resolve(initialExerciseProgress),
      ]).then(([exerciseInfoResponse, exerciseProgressResponse]) => {
         setExerciseInfo(exerciseInfoResponse);

         if (moduleId) {
            const moduleProgress =
               exerciseProgressResponse.modules[moduleId] ??
               initialExerciseProgress;
            setExerciseProgressSummary({
               ...moduleProgress,
               locked:
                  exerciseProgressResponse.currentLevel <=
                  exerciseInfoResponse.unlockAfter,
            });
         } else {
            setExerciseProgressSummary(exerciseProgressResponse);
         }

         setLoadingNew(false);
      });
   }, [exerciseId, moduleInfoPath, isAuthenticated]);

   // if (exerciseInfo == null || exerciseProgress == null) {
   //    return <ShowSiteLoading />;
   // }
   if (exerciseInfo == null) {
      return <ShowSiteLoading />;
   }

   return (
      <ExerciseInfoStyles className="narrow">
         <Title
            title={exerciseInfo.title}
            subtitle={exerciseInfo.subtitle}
            excludeBranding={exerciseInfo.subtitle}
            description={exerciseInfo.seoDescription}
         />
         <h1>{exerciseInfo.title}</h1>
         <article>
            <section className="exercise-info-progress-description">
               <div>
                  <ExerciseIcon iconId={exerciseInfo.iconId} />
               </div>
               <div>
                  <div>
                     {exerciseInfo.longDescription
                        .split('\n')
                        .map((text, i) => (
                           // Disabled b/c order won't change
                           // eslint-disable-next-line react/no-array-index-key
                           <p key={i}>{text}</p>
                        ))}
                  </div>
                  <div className="exercise-start-button-wrapper">
                     {exerciseProgressSummary.locked ? (
                        <small>
                           Unlock this module by completing Level{' '}
                           {exerciseInfo.unlockAfter} of{' '}
                           <Link
                              className="link"
                              to={`/app/catalog/exercise/${exerciseId}`}
                           >
                              the primary exercise
                           </Link>
                           .
                        </small>
                     ) : (
                        <Button
                           as={Link}
                           className="button-cta"
                           to={`/app/exercise/${exerciseId}${moduleExercisePath}`}
                        >
                           {hasProgress ? 'Continue' : 'Start'} Training
                        </Button>
                     )}
                  </div>
               </div>
            </section>
         </article>
         <section className="exercise-info-levels">
            <h2>Levels</h2>
            <LevelsList levels={mappedLevels} />
         </section>

         {exerciseInfo.modulesList && exerciseInfo.modulesList.length > 0 && (
            <section>
               <h2>Modules</h2>
               <ModulesList
                  exerciseId={exerciseId}
                  modules={exerciseInfo.modulesList}
                  mainExerciseLevel={exerciseProgressSummary?.currentLevel}
                  moduleProgress={exerciseProgressSummary.modules}
               />
            </section>
         )}
         {loadingNew && <ShowSiteLoading />}
      </ExerciseInfoStyles>
   );
});

export { ExerciseInfoScreen, ExerciseInfoStyles };
